import { Link } from "react-router-dom";
import { Phone, ArrowRight, Droplet } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ServiceCard from "@/components/ServiceCard";
import FAQ from "@/components/FAQ";
import { PHONE, PHONE_LINK, PURIFICATION_SERVICES, BUSINESS_NAME, AREA_SERVED_SCHEMA, SOCIAL_PROFILES } from "@/lib/constants";

const waterFaqs = [
  {
    question: "Why is Arizona water so hard?",
    answer:
      "Arizona's water comes primarily from underground aquifers and the Colorado River, which naturally contain high levels of dissolved minerals like calcium and magnesium. The desert climate and lack of rainfall mean water travels through mineral-rich rock for extended periods, picking up these minerals. Tolleson and the West Valley consistently rank among the highest for water hardness in the nation.",
  },
  {
    question: "What's the difference between a water softener and a filtration system?",
    answer:
      "Water softeners specifically target hard water minerals (calcium and magnesium) using ion exchange technology. Filtration systems remove a broader range of contaminants like chlorine, sediment, and organic compounds. Many Arizona homes benefit from both: a softener to protect pipes and appliances, plus filtration for better-tasting drinking water.",
  },
  {
    question: "How long does a water softener last?",
    answer:
      "A quality water softener typically lasts 15-20 years with proper maintenance. This includes regularly adding salt, occasional resin bed cleaning, and annual inspections. We offer maintenance plans to help extend the life of your system.",
  },
  {
    question: "Is reverse osmosis water safe to drink?",
    answer:
      "Yes, reverse osmosis (RO) produces some of the purest drinking water available. RO systems remove up to 99% of contaminants including lead, arsenic, chlorine, and dissolved solids. The result is clean, great-tasting water right from your tap.",
  },
];

const WaterPurification = () => {
  const baseUrl = "https://truamericanplumbing.com";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: waterFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Water Purification",
    provider: { "@type": "Plumber", "@id": `${baseUrl}/#organization`, name: BUSINESS_NAME, sameAs: SOCIAL_PROFILES },
    areaServed: AREA_SERVED_SCHEMA,
    url: `${baseUrl}/water-purification`,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Water Purification Services",
      itemListElement: PURIFICATION_SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title, url: `${baseUrl}/water-purification/${s.slug}` },
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
      { "@type": "ListItem", position: 2, name: "Water Purification", item: `${baseUrl}/water-purification` },
    ],
  };

  return (
    <>
      <SEO
        title="Water Purification & Softeners in Tolleson, AZ"
        description="Combat Arizona hard water with professional water softeners, whole home filtration, and reverse osmosis systems. Expert installation in Tolleson & West Valley. Call (480) 322-2567."
        canonicalUrl="/water-purification/"
        schema={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Breadcrumb */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span>/</span>
            <span className="text-foreground">Water Purification</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Droplet className="h-4 w-4" />
              Arizona Hard Water Experts
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Water{" "}
              <span className="text-gradient-copper">Purification Solutions</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Arizona has some of the hardest water in the country. Protect your
              home, appliances, and family with professional water treatment
              systems installed by certified technicians.
            </p>
            <a href={PHONE_LINK}>
              <Button variant="hero" size="xl" className="gap-2">
                <Phone className="h-5 w-5" />
                Free Water Assessment: {PHONE}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Water Treatment <span className="text-gradient-copper">Services</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {PURIFICATION_SERVICES.map((service) => (
              <ServiceCard
                key={service.slug}
                {...service}
                basePath="/water-purification"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Hard Water Info */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Signs of <span className="text-gradient-copper">Hard Water</span> in Your Home
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    White scale buildup on faucets, showerheads, and fixtures
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    Spots and film on glasses and dishes after washing
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    Dry, itchy skin and dull, brittle hair
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    Clothes fading and wearing out faster than normal
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    Reduced water pressure from mineral buildup in pipes
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-primary flex-shrink-0" />
                  <p className="text-foreground/80">
                    Higher energy bills from water heater inefficiency
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ faqs={waterFaqs} />

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/20 via-background to-background">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">
            Get a Free Water Quality Assessment
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Find out exactly what's in your water and get personalized
            recommendations for your home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK}>
              <Button variant="hero" size="lg" className="gap-2">
                <Phone className="h-4 w-4" />
                {PHONE}
              </Button>
            </a>
            <Link to="/contact/">
              <Button variant="outline" size="lg" className="gap-2">
                Schedule Assessment
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WaterPurification;
